import axios from 'axios';
import { Message } from 'element-ui';
import router from '../router';

// 错误码映射表
const ERROR_MESSAGES = {
    400: '请求参数错误',
    401: '未登录或登录已过期',
    404: '请求的资源不存在',
    500: '服务器内部错误',
    502: '网关错误',
    503: '服务不可用',
    504: '网关超时',
    // 业务逻辑错误
    1001: '参数校验失败',
    1002: '操作失败',
    1003: '用户名已存在',
    1004: '用户名或密码错误',
    1005: '用户不存在',
    1006: '手机号已存在',
    1007: '邮箱已存在',
    1008: '服务器异常'
};

// 创建axios实例
//baseURL: process.env.NODE_ENV === 'production' ? 'http://localhost:8090' : 'http://localhost:8090',

const baseURL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8090';

const service = axios.create({
    baseURL,
    timeout: 10000,
});

const refreshService = axios.create({
    baseURL,
    timeout: 10000,
});

// 请求重试配置
service.defaults.retry = 2; // 重试次数
service.defaults.retryDelay = 1000; // 重试延迟
service.defaults.shouldRetry = (error) => {
    // 定义什么样的错误需要重试，这里以网络错误和500为例
    return error.code === 'ECONNABORTED' ||
           error.response?.status === 500 ||
           !error.response; // 网络错误
};

// Token服务
export const TokenService = {
    refreshTokenEndpoint: '/api/members/refresh-token',
    getTokenPayload(token) {
        if (!token) return null;
        try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join(''));
            return JSON.parse(jsonPayload);
        } catch (e) {
            return null;
        }
    },
    getTokenRemainingMs(token) {
        const payload = this.getTokenPayload(token);
        const exp = payload?.exp;
        if (!exp) return 0;
        return Math.max(0, exp * 1000 - Date.now());
    },
    isTokenExpired(token) {
        return this.getTokenRemainingMs(token) <= 0;
    },
    isTokenExpiringSoon(token, withinSeconds = 120) {
        return this.getTokenRemainingMs(token) > 0 && this.getTokenRemainingMs(token) <= withinSeconds * 1000;
    },
    async refreshToken() {
        const token = this.getToken();
        if (!token) return null;
        try {
            const response = await refreshService.post(
                this.refreshTokenEndpoint,
                null,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            const newToken = response?.data?.data?.token;
            if (newToken) {
                this.saveToken(newToken);
                return newToken;
            }
            return null;
        } catch (error) {
            console.error('Error refreshing token:', error);
            return null;
        }
    },
    getToken() {
        return localStorage.getItem('userToken');
    },
    saveToken(token) {
        localStorage.setItem('userToken', token);
    },
    removeToken() {
        localStorage.removeItem('userToken');
    },
    getAuthHeader() {
        return `Bearer ${this.getToken()}`;
    }
};

// 请求拦截器
service.interceptors.request.use(
    async (config) => {
        // 对于登录和注册接口，跳过token处理
        const isAuthEndpoint = config.url.includes('/login') || config.url.includes('/register') || config.url.includes('/refresh-token');

        if (!isAuthEndpoint) {
            let token = TokenService.getToken();

            if (token && TokenService.isTokenExpired(token)) {
                TokenService.removeToken();
                localStorage.removeItem('userInfo');
                router.push('/login');
                return Promise.reject(new Error('Token expired'));
            }

            if (token && TokenService.isTokenExpiringSoon(token)) {
                const newToken = await TokenService.refreshToken();
                if (newToken) token = newToken;
            }

            if (token) {
                config.headers['Authorization'] = TokenService.getAuthHeader();
            }
        }
        return config;
    },
    (error) => {
        console.error('Request error:', error);
        return Promise.reject(error);
    }
);

// 响应拦截器
service.interceptors.response.use(
    (response) => {
        // 对于文件下载请求，直接返回响应对象
        if (response.config.responseType === 'blob') {
            return response;
        }

        const res = response.data;

        if (res.code === 200) {
            // 对于登录接口，直接返回data中的内容
            if (response.config.url && response.config.url.includes('/login')) {
                return res.data;
            }
            // 对于其他接口，返回完整的响应对象以保持兼容性
            return res;
        }

        // 定义错误消息
        const errorMessage = res.message || ERROR_MESSAGES[res.code] || '未知错误';

        // 统一错误处理 - 403错误静默处理
        if (res.code !== 403) {
            Message({
                message: errorMessage,
                type: 'error',
                duration: 5000
            });
        }

        // 特殊错误码处理
        if (res.code === 401) {
            // 清除用户信息
            TokenService.removeToken();
            localStorage.removeItem('userInfo');
            // 跳转登录页并保存当前路由
            const currentPath = router.currentRoute.fullPath;
            router.push({
                path: '/login',
                query: { redirect: currentPath }
            });
        }

        return Promise.reject(new Error(errorMessage));
    },
    (error) => {
        // 处理请求重试
        const config = error.config;
        // 如果是401错误，不进行重试
        if (error.response?.status === 401) {
            TokenService.removeToken();
            localStorage.removeItem('userInfo');
            const currentPath = router.currentRoute.fullPath;
            router.push({
                path: '/login',
                query: { redirect: currentPath }
            });
            return Promise.reject(error);
        }

        if (!config || !config.retry || config.retry <= 0) {
            return handleError(error);
        }

        // 判断是否需要重试
        if (!service.defaults.shouldRetry(error)) {
            return handleError(error);
        }

        config.__retryCount = config.__retryCount || 0;
        if (config.__retryCount >= config.retry) {
            return handleError(error);
        }

        config.__retryCount += 1;
        const backoff = new Promise((resolve) => {
            setTimeout(() => {
                resolve();
            }, config.retryDelay || 1);
        });

        return backoff.then(() => service(config));
    }
);

// 统一错误处理函数
function handleError(error) {
    console.error('Response Error:', error);

    // 处理网络错误
    if (!error.response) {
        Message({
            message: '网络连接失败，请检查网络设置',
            type: 'error',
            duration: 5000
        });
        return Promise.reject(error);
    }

    // 处理HTTP错误 - 403错误静默处理
    if (error.response.status !== 403) {
        const errorMessage = ERROR_MESSAGES[error.response.status] || '请求失败';
        Message({
            message: errorMessage,
            type: 'error',
            duration: 5000
        });
    }

    return Promise.reject(error);
}

export default service;
